import React, { useEffect, useState, useMemo } from "react";
import {
  FileText, Upload, Loader2, Plus, ArrowDownCircle, ArrowUpCircle, X, Link2, Trash2,
  Package, DollarSign, Boxes, Building2, CheckCircle2, AlertTriangle, Ban, Sparkles, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { api, brl, fmtDate, errMsg } from "@/lib/api";

const STATUS_BADGE = {
  processada: "bg-emerald-50 text-emerald-700",
  confirmada: "bg-emerald-50 text-emerald-700",
  rascunho: "bg-amber-50 text-amber-700",
  cancelada: "bg-rose-50 text-rose-700",
};
const FIN_BADGE = {
  quitado: ["Quitado", "bg-emerald-50 text-emerald-700"],
  pendente: ["Pendente", "bg-amber-50 text-amber-700"],
  vencido: ["Vencido", "bg-rose-50 text-rose-700"],
  cancelado: ["Cancelado", "bg-slate-100 text-slate-500"],
  sem_lancamento: ["—", "bg-slate-100 text-slate-400"],
};

export default function NotaFiscal() {
  const [tab, setTab] = useState("todas");
  const [list, setList] = useState(null);
  const [importData, setImportData] = useState(null); // parse result
  const [importing, setImporting] = useState(false);
  const [saidaModal, setSaidaModal] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    const p = tab === "todas" ? {} : { tipo: tab === "entrada" ? "entrada" : "saida" };
    api.get("/notas", { params: p }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [tab]);

  const onXml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const xml = await file.text();
      const { data } = await api.post("/notas/entrada/parse", { xml });
      const itens = data.itens.map((it) => ({
        ...it,
        acao: it.acao_sugerida,
        produto: it.sugestao ? { ...it.sugestao, categoria: it.ia?.categoria_sugerida || "", marca: it.ia?.marca_sugerida || "" } : null,
      }));
      setImportData({ ...data, itens });
      if (data.nota_duplicada) toast.warning("Atenção: já existe uma NF com essa chave.");
    } catch (err) { toast.error(errMsg(err)); } finally { setImporting(false); e.target.value = ""; }
  };

  const confirmarEntrada = async () => {
    try {
      const payload = {
        nfe: importData.nfe,
        itens: importData.itens.map((it) => ({
          acao: it.acao, produto_id: it.produto_id, descricao: it.descricao,
          quantidade: it.quantidade, valor_unitario: it.valor_unitario, valor_total: it.valor_total,
          ncm: it.ncm, cfop: it.cfop, unidade: it.unidade, ean: it.ean,
          produto: it.acao === "criar" ? it.produto : undefined,
        })),
      };
      await api.post("/notas/entrada/confirmar", payload);
      toast.success("NF de entrada lançada: estoque e contas a pagar atualizados");
      setImportData(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = list;

  return (
    <div className="animate-fade-up">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900"><FileText className="h-6 w-6 text-sky-600" /> Notas Fiscais</h1>
          <p className="text-sm text-slate-500">Hub de integração: Produtos · Estoque · Contas a Pagar/Receber · Fluxo de Caixa</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label data-testid="nfe-import-xml-btn" className="flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importar XML (entrada)
            <input type="file" accept=".xml,text/xml" className="hidden" onChange={onXml} disabled={importing} />
          </label>
          <button data-testid="nfe-nova-saida-btn" onClick={() => setSaidaModal(true)} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"><Plus className="h-4 w-4" /> Nova NF de saída</button>
        </div>
      </div>

      <div className="mb-4 flex gap-2">
        {[["todas", "Todas"], ["entrada", "📥 Entrada"], ["saida", "📤 Saída"]].map(([k, l]) => (
          <button key={k} data-testid={`nfe-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-xs font-bold ${tab === k ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>

      {!filtered ? (
        <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-500">Nenhuma nota fiscal ainda. Importe um XML de entrada ou crie uma NF de saída.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3">Tipo</th><th className="p-3">Número</th><th className="p-3">Fornecedor / Cliente</th>
                <th className="p-3">Emissão</th><th className="p-3 text-right">Valor</th>
                <th className="p-3">Status</th><th className="p-3">Financeiro</th><th className="p-3">Fiscal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((n) => {
                const [finL, finC] = FIN_BADGE[n.financeiro_status] || FIN_BADGE.sem_lancamento;
                return (
                  <tr key={n.id} data-testid="nfe-row" onClick={() => setDetail(n.id)} className="cursor-pointer hover:bg-slate-50">
                    <td className="p-3">{n.tipo === "entrada" ? <span className="flex items-center gap-1 font-semibold text-emerald-700"><ArrowDownCircle className="h-4 w-4" />Entrada</span> : <span className="flex items-center gap-1 font-semibold text-sky-700"><ArrowUpCircle className="h-4 w-4" />Saída</span>}</td>
                    <td className="p-3 font-mono text-slate-700">{n.numero || "—"}{n.serie ? `-${n.serie}` : ""}</td>
                    <td className="p-3 font-medium text-slate-800">{(typeof n.fornecedor === "object" ? n.fornecedor?.nome : n.fornecedor) || (typeof n.cliente === "object" ? n.cliente?.nome : n.cliente) || "—"}</td>
                    <td className="p-3 text-slate-500">{fmtDate(n.emissao)}</td>
                    <td className="p-3 text-right font-mono font-semibold">{brl(n.valor_total)}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[n.status] || "bg-slate-100 text-slate-600"}`}>{n.status}</span></td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${finC}`}>{finL}</span></td>
                    <td className="p-3 text-[11px] text-slate-500">{n.fiscal_status === "pendente_integracao_fiscal" ? "⏳ Pend. integração" : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {importData && <ImportReview data={importData} setData={setImportData} onConfirm={confirmarEntrada} onClose={() => setImportData(null)} />}
      {saidaModal && <SaidaModal onClose={() => setSaidaModal(false)} onDone={() => { setSaidaModal(false); load(); }} />}
      {detail && <NotaDetail id={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function ImportReview({ data, setData, onConfirm, onClose }) {
  const setItem = (i, patch) => setData({ ...data, itens: data.itens.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const setProd = (i, patch) => setData({ ...data, itens: data.itens.map((it, idx) => (idx === i ? { ...it, produto: { ...it.produto, ...patch } } : it)) });
  const total = data.nfe.valor_total;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl" data-testid="nfe-import-review">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Revisar NF-e de entrada</h3>
            <p className="text-xs text-slate-500">Fornecedor: <strong>{data.nfe.emitente?.nome || "—"}</strong> · NF {data.nfe.numero} · {fmtDate(data.nfe.emissao)} · {brl(total)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        {data.nota_duplicada && (
          <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-700"><AlertTriangle className="h-4 w-4" /> Já existe uma NF com essa chave (nº {data.nota_duplicada.numero}). Confirme apenas se realmente for um novo lançamento.</div>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {data.itens.map((it, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid="nfe-import-item">
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">{it.descricao}</div>
                  <div className="text-[11px] text-slate-500">Qtd: {it.quantidade} {it.unidade} · Custo unit.: {brl(it.valor_unitario)} · NCM {it.ncm || "—"} · EAN {it.ean || "—"}</div>
                </div>
                {it.produto_id ? (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Vinculado ({it.match_by})</span>
                ) : (
                  <span className="flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700"><Sparkles className="h-3 w-3" /> Novo produto</span>
                )}
              </div>

              {!it.produto_id && it.possivel_duplicidade?.length > 0 && (
                <div className="mb-2 rounded bg-amber-50 p-2 text-[11px] text-amber-700">
                  <ShieldAlert className="mr-1 inline h-3 w-3" /> Possível duplicidade: {it.possivel_duplicidade.map((c) => c.descricao).join("; ")}
                </div>
              )}
              {it.ia?.inconsistencias?.length > 0 && (
                <div className="mb-2 rounded bg-rose-50 p-2 text-[11px] text-rose-700">Inconsistências (IA): {it.ia.inconsistencias.join("; ")}</div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {["vincular", "criar", "ignorar"].map((a) => {
                  const disabled = a === "vincular" && !it.produto_id;
                  return (
                    <button key={a} disabled={disabled} data-testid={`nfe-item-acao-${i}-${a}`} onClick={() => setItem(i, { acao: a })}
                      className={`rounded px-2.5 py-1 text-[11px] font-semibold ${it.acao === a ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"} ${disabled ? "cursor-not-allowed opacity-40" : ""}`}>
                      {a === "vincular" ? "Vincular existente" : a === "criar" ? "Criar produto" : "Ignorar"}
                    </button>
                  );
                })}
              </div>

              {it.acao === "criar" && it.produto && (
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[["descricao", "Descrição"], ["sku", "SKU/Código"], ["categoria", "Categoria"], ["marca", "Marca"], ["unidade", "Unidade"], ["ncm", "NCM"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="block text-[10px] font-semibold uppercase text-slate-400">{l}{it.ia?.campos_ausentes?.includes(k) ? " ⚠️" : ""}</label>
                      <input value={it.produto[k] ?? ""} onChange={(e) => setProd(i, { [k]: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-xs" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 p-4">
          <p className="text-[11px] text-slate-500">Ao confirmar: cria/vincula produtos → entrada no estoque → gera contas a pagar ({data.nfe.parcelas?.length || 1} parcela(s)).</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
            <button data-testid="nfe-confirm-entrada-btn" onClick={onConfirm} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Confirmar e lançar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SaidaModal({ onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [cliente, setCliente] = useState("");
  const [oppId, setOppId] = useState("");
  const [emissao, setEmissao] = useState(new Date().toISOString().slice(0, 10));
  const [itens, setItens] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/opportunities", { params: { saved: true, page_size: 100 } }).then((r) => setOpps(r.data.items || [])).catch(() => {});
  }, []);

  const addItem = () => setItens([...itens, { produto_id: "", quantidade: 1, valor_unitario: 0 }]);
  const setItem = (i, patch) => setItens(itens.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const rmItem = (i) => setItens(itens.filter((_, idx) => idx !== i));

  const prodOf = (id) => products.find((p) => p.id === id);
  const total = useMemo(() => itens.reduce((s, it) => s + (parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0), 0), [itens]);

  const submit = async (confirmar) => {
    if (!itens.length) { toast.error("Adicione ao menos um item."); return; }
    setSaving(true);
    try {
      const body = {
        cliente, opportunity_id: oppId || null, emissao,
        itens: itens.map((it) => ({ produto_id: it.produto_id, descricao: prodOf(it.produto_id)?.descricao, quantidade: parseFloat(it.quantidade) || 0, valor_unitario: parseFloat(it.valor_unitario) || 0 })),
      };
      const { data } = await api.post("/notas/saida", body);
      if (confirmar) {
        await api.post(`/notas/saida/${data.id}/confirmar`);
        toast.success("NF de saída confirmada: estoque baixado e conta a receber gerada");
      } else {
        toast.success("NF de saída salva como rascunho");
      }
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.faltas) {
        toast.error(`Estoque insuficiente: ${d.faltas.map((f) => `${f.descricao} (tem ${f.disponivel}, precisa ${f.necessario})`).join("; ")}`);
      } else { toast.error(errMsg(e)); }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl" data-testid="nfe-saida-modal">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Nova NF de saída</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><label className="mb-1 block text-xs font-semibold text-slate-600">Cliente / Órgão</label><input data-testid="nfe-saida-cliente" value={cliente} onChange={(e) => setCliente(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-600">Emissão</label><input type="date" value={emissao} onChange={(e) => setEmissao(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-600">Vincular a licitação (opcional)</label>
              <select value={oppId} onChange={(e) => setOppId(e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
                <option value="">— Nenhuma —</option>
                {opps.map((o) => <option key={o.id} value={o.id}>{(o.orgao_razao_social || "").slice(0, 40)} · {(o.objeto || "").slice(0, 30)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600">Itens</span>
              <button data-testid="nfe-saida-add-item" onClick={addItem} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"><Plus className="h-3 w-3" /> Item</button>
            </div>
            <div className="space-y-2">
              {itens.map((it, i) => {
                const p = prodOf(it.produto_id);
                const disp = p ? Number(p.estoque || 0) : null;
                const insuf = disp != null && disp < (parseFloat(it.quantidade) || 0);
                return (
                  <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 p-2" data-testid="nfe-saida-item">
                    <select value={it.produto_id} onChange={(e) => setItem(i, { produto_id: e.target.value, valor_unitario: prodOf(e.target.value)?.preco || it.valor_unitario })} className="col-span-5 rounded border border-slate-200 px-2 py-1.5 text-xs">
                      <option value="">Selecione o produto…</option>
                      {products.map((pp) => <option key={pp.id} value={pp.id}>{pp.descricao} (estoque {pp.estoque || 0})</option>)}
                    </select>
                    <input type="number" value={it.quantidade} onChange={(e) => setItem(i, { quantidade: e.target.value })} placeholder="Qtd" className={`col-span-2 rounded border px-2 py-1.5 text-xs ${insuf ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} />
                    <input type="number" value={it.valor_unitario} onChange={(e) => setItem(i, { valor_unitario: e.target.value })} placeholder="Preço unit." className="col-span-2 rounded border border-slate-200 px-2 py-1.5 text-xs" />
                    <div className="col-span-2 text-right font-mono text-xs font-semibold">{brl((parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0))}</div>
                    <button onClick={() => rmItem(i)} className="col-span-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    {insuf && <div className="col-span-12 text-[10px] font-semibold text-rose-600">⚠️ Estoque insuficiente (disponível: {disp})</div>}
                  </div>
                );
              })}
              {itens.length === 0 && <p className="text-xs text-slate-400">Nenhum item. Clique em "Item" para adicionar.</p>}
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <div className="flex justify-between font-semibold text-slate-800"><span>Valor total</span><span className="font-mono">{brl(total)}</span></div>
            <p className="mt-1 text-[11px] text-amber-700">⏳ NF de saída fica como <strong>PENDENTE DE INTEGRAÇÃO FISCAL</strong> (sem emissão oficial). Ao confirmar, o estoque é baixado e a conta a receber é gerada.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 p-4">
          <button onClick={() => submit(false)} disabled={saving} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-60">Salvar rascunho</button>
          <button data-testid="nfe-saida-confirmar-btn" onClick={() => submit(true)} disabled={saving} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar e baixar estoque</button>
        </div>
      </div>
    </div>
  );
}

function NotaDetail({ id, onClose, onChanged }) {
  const [n, setN] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/notas/${id}`).then((r) => setN(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]);

  const confirmarSaida = async () => {
    setBusy(true);
    try { await api.post(`/notas/saida/${id}/confirmar`); toast.success("Estoque baixado e conta a receber gerada"); load(); onChanged(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(d?.msg ? `${d.msg}` : errMsg(e)); } finally { setBusy(false); }
  };
  const cancelar = async () => {
    if (!window.confirm("Cancelar esta nota? Movimentos de estoque e contas vinculadas serão estornados.")) return;
    setBusy(true);
    try { await api.post(`/notas/${id}/cancelar`); toast.success("Nota cancelada e movimentos estornados"); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl" data-testid="nfe-detail-drawer">
        {!n ? (
          <div className="grid flex-1 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">{n.tipo === "entrada" ? <ArrowDownCircle className="h-5 w-5 text-emerald-600" /> : <ArrowUpCircle className="h-5 w-5 text-sky-600" />} NF {n.tipo} · {n.numero || "s/nº"}</div>
                <div className="text-xs text-slate-500">{(typeof n.fornecedor === "object" ? n.fornecedor?.nome : n.fornecedor) || (typeof n.cliente === "object" ? n.cliente?.nome : n.cliente) || "—"} · {fmtDate(n.emissao)} · {brl(n.valor_total)}</div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[n.status] || "bg-slate-100 text-slate-600"}`}>Status: {n.status}</span>
                {n.fiscal_status === "pendente_integracao_fiscal" && <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">⏳ Pendente de integração fiscal</span>}
                {n.chave && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-mono text-slate-500">chave …{String(n.chave).slice(-8)}</span>}
              </div>

              {n._opportunity && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs">
                  <div className="flex items-center gap-1 font-semibold text-sky-700"><Link2 className="h-3.5 w-3.5" /> Licitação vinculada</div>
                  <div className="mt-1 text-slate-700">{n._opportunity.orgao_razao_social} — {n._opportunity.objeto?.slice(0, 90)}</div>
                </div>
              )}

              <Section icon={Package} title="Itens & Produtos">
                {n.itens?.map((it, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-0">
                    <span className="min-w-0 truncate text-slate-700">{it.descricao} <span className="text-slate-400">×{it.quantidade}</span></span>
                    <span className="font-mono text-xs text-slate-600">{brl(it.valor_total)}</span>
                  </div>
                ))}
              </Section>

              <Section icon={Boxes} title="Movimentos de estoque">
                {n._movimentos?.length ? n._movimentos.map((m) => (
                  <div key={m.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs last:border-0">
                    <span className={m.tipo === "entrada" ? "text-emerald-700" : "text-sky-700"}>{m.tipo === "entrada" ? "+" : "−"}{m.quantidade} {m.produto_descricao?.slice(0, 30)}</span>
                    <span className="text-slate-400">saldo: {m.saldo_apos}</span>
                  </div>
                )) : <p className="text-xs text-slate-400">Sem movimentos.</p>}
              </Section>

              <Section icon={DollarSign} title="Financeiro vinculado">
                {[...(n._payables || []).map((p) => ["Pagar", p]), ...(n._receivables || []).map((r) => ["Receber", r])].map(([kind, a]) => (
                  <div key={a.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs last:border-0">
                    <span className="text-slate-600">{kind} · venc. {fmtDate(a.vencimento)} · {a.parcela ? `parc. ${a.parcela}` : ""}</span>
                    <span className="flex items-center gap-2"><span className="font-mono">{brl(a.valor)}</span><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${a.status === "pendente" ? "bg-amber-50 text-amber-700" : a.status === "cancelado" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span></span>
                  </div>
                ))}
                {!(n._payables?.length || n._receivables?.length) && <p className="text-xs text-slate-400">Sem lançamentos financeiros.</p>}
              </Section>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-slate-200 p-4">
              <button data-testid="nfe-cancelar-btn" onClick={cancelar} disabled={busy || n.status === "cancelada"} className="flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40"><Ban className="h-4 w-4" /> Cancelar nota</button>
              {n.tipo === "saida" && n.status === "rascunho" && (
                <button data-testid="nfe-detail-confirmar-saida" onClick={confirmarSaida} disabled={busy} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar e baixar estoque</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><Icon className="h-3.5 w-3.5" /> {title}</div>
      {children}
    </div>
  );
}
